/* eslint-disable */




import {
    VERB_INTENT_SEEDS,
    SINGLE_TOKEN_BOOSTS,
    ROUTE_CONTEXT_BIAS,
} from './dictionaries';
import { tokensIncludeAll } from './tokenizer';

function _nullishCoalesce(lhs, rhsFn) { if (lhs != null) { return lhs; } else { return rhsFn(); } }

/**
 * Score all possible intents given tokens, entities, and page context.
 * Returns candidates sorted by score descending.
 * Each candidate carries a `reasons[]` trace for debuggability.
 */
export function scoreIntents(
    tokens,
    entities,
    ctx,
) {
    const scores = new Map();

    const bump = (intent, delta, reason) => {
        // Scoping: Only score intents that are explicitly allowed (if the filter is provided)
        if (ctx.allowed_intents && !ctx.allowed_intents.includes(intent)) {
            return;
        }

        const existing = _nullishCoalesce(scores.get(intent), () => ( { score: 0, reasons: [] }));
        scores.set(intent, {
            score: existing.score + delta,
            reasons: [...existing.reasons, reason],
        });
    };

    // ── Phase 1: Verb+keyword multi-token seeds ────────────────────────────────
    for (const seed of VERB_INTENT_SEEDS) {
        if (tokensIncludeAll(tokens, seed.tokens)) {
            bump(seed.intent, seed.score, seed.reason);
        }
    }

    // ── Phase 2: Single-token boosts ──────────────────────────────────────────
    for (const rule of SINGLE_TOKEN_BOOSTS) {
        if (tokens.includes(rule.token)) {
            bump(rule.intent, rule.score, rule.reason);
        }
    }

    // ── Phase 3: Entity presence bonuses ──────────────────────────────────────
    if (entities.policy_id) {
        // Policy ID strongly favors detail-level intents
        bump('policy_open', 25, 'entity:policy_id present → policy_open boost');
        bump('policy_export_pdf', 20, 'entity:policy_id present → export boost');
        bump('policy_show_acknowledgements', 20, 'entity:policy_id present → acks boost');
        bump('policy_show_approvals', 20, 'entity:policy_id present → approvals boost');
        bump('policy_show_revision_history', 20, 'entity:policy_id present → revisions boost');
        // And suppress generic navigation
        bump('navigate', -20, 'entity:policy_id present → suppress navigate');
    }

    if (entities.control_ids.length > 0) {
        bump('policy_framework_mapping_view', 35, 'entity:control_ids present → mapping view boost');
    }

    if (entities.frameworks.length > 0) {
        bump('policy_framework_mapping_view', 20, 'entity:frameworks present → mapping view boost');
    }

    if (entities.time_range) {
        bump('policy_search', 20, 'entity:time_range present → search boost');
    }

    if (entities.template_query) {
        bump('template_search', 25, 'entity:template_query extracted → template_search boost');
        bump('policy_create_from_template', 10, 'entity:template_query → weak create hint');
    }

    if (entities.search_query) {
        bump('policy_search', 50, 'entity:search_query extracted → policy_search boost');
        bump('navigate', -30, 'entity:search_query present → suppress navigate');
    }

    if (entities.policy_title) {
        bump('policy_open', 50, 'entity:policy_title extracted → policy_open boost');
        // suppress navigate when we have a specific title
        bump('navigate', -10, 'entity:policy_title → suppress plain navigate');
    }

    // "policies" keyword without specific detail verbs → navigate list
    const tokenStr = tokens.join(' ');
    if (tokenStr === 'policies' || (tokens.includes('policies') && tokens.length === 1)) {
        bump('navigate', 40, 'single keyword:policies → navigate to list');
    }
    if (tokenStr === 'templates' || (tokens.includes('templates') && tokens.length === 1)) {
        bump('navigate', 40, 'single keyword:templates → navigate to templates');
    }

    // ── Phase 4: Context (route) bias ─────────────────────────────────────────
    for (const rule of ROUTE_CONTEXT_BIAS) {
        if (rule.routePattern.test(ctx.current_route)) {
            for (const boost of rule.intentBoosts) {
                bump(boost.intent, boost.boost, boost.reason);
            }
        }
    }

    // ── Phase 5: page_context bias ────────────────────────────────────────────
    if (ctx.page_context === 'detail') {
        bump('policy_export_pdf', 15, 'page_context:detail → export boost');
        bump('policy_show_acknowledgements', 15, 'page_context:detail → acks boost');
        bump('policy_show_approvals', 15, 'page_context:detail → approvals boost');
        bump('policy_show_revision_history', 15, 'page_context:detail → revisions boost');
    }
    if (ctx.page_context === 'list') {
        bump('policy_search', 15, 'page_context:list → search boost');
    }
    if (ctx.page_context === 'templates') {
        bump('template_search', 15, 'page_context:templates → template search boost');
        bump('policy_create_from_template', 10, 'page_context:templates → create boost');
    }

    // Convert map to sorted array
    const candidates = [];
    for (const [intent, { score, reasons }] of scores.entries()) {
        if (score > 0) {
            candidates.push({ intent, score, reasons });
        }
    }

    return candidates.sort((a, b) => b.score - a.score);
}
